// =============================================================================
// src/features/crisis/crisis-draft.ts
// Geracao do rascunho da nota de esclarecimento (skeleton -> texto -> acoes).
//
// Proveniencia: porte FIEL de assets/crisis.js 410-453 (crzGenBtn). Clicar
//   mostra um skeleton e, apos um delay (1300ms, ou 250ms com reduce-motion),
//   substitui pelo rascunho com botoes Copiar/Editar/Regerar. "Regerar" re-aciona
//   o proprio botao (genBtn.click()). "Copiar" usa navigator.clipboard com
//   fallback silencioso e confirma "Copiado" por 1600ms.
//
// SRP: so a geracao do rascunho. O estado `generating` evita disparos
//   concorrentes (porte do flag homonimo do legado).
// =============================================================================

import { on, qs } from '../../app/dom';

/** Delay do rascunho em ms: normal vs. prefers-reduced-motion (assets/crisis.js 423). */
const DRAFT_DELAY_MS = { full: 1300, reduced: 250 } as const;

/** Duracao do feedback "Copiado" antes de voltar a "Copiar" (assets/crisis.js 448). */
const COPY_FEEDBACK_MS = 1600;

/** Icones Tabler inline do rascunho (assets/crisis.js 328-334). */
const TI = {
  refresh: '<i class="ti ti-refresh crz-ico sm" aria-hidden="true"></i>',
  pencil: '<i class="ti ti-pencil crz-ico sm" aria-hidden="true"></i>',
  copy: '<i class="ti ti-copy crz-ico sm" aria-hidden="true"></i>',
  check: '<i class="ti ti-check crz-ico sm" aria-hidden="true"></i>',
  spark: '<i class="ti ti-sparkles crz-ico sm" aria-hidden="true"></i>',
} as const;

/**
 * Liga o botao "Gerar rascunho da nota". No-op se #crzGenBtn/#crzDraftSlot
 * faltarem (paridade com o acesso direto do legado).
 */
export function bindCrisisDraft(reduceMotion: boolean): void {
  const button = qs<HTMLButtonElement>('#crzGenBtn');
  const slot = qs<HTMLElement>('#crzDraftSlot');
  if (!button || !slot) return;
  new CrisisDraftGenerator(button, slot, reduceMotion).bind();
}

/** Gera o rascunho com guarda contra disparos concorrentes (assets/crisis.js 413). */
class CrisisDraftGenerator {
  private generating = false;

  constructor(
    private readonly button: HTMLButtonElement,
    private readonly slot: HTMLElement,
    private readonly reduceMotion: boolean,
  ) {}

  /** Liga o clique do botao gerador (assets/crisis.js 415). */
  bind(): void {
    on(this.button, 'click', () => this.generate());
  }

  /** Mostra o skeleton e agenda a troca pelo rascunho (assets/crisis.js 415-453). */
  private generate(): void {
    if (this.generating) return;
    this.generating = true;
    this.button.disabled = true;
    this.button.style.opacity = '0.7';
    this.slot.innerHTML = DRAFT_SKELETON_HTML;
    const delay = this.reduceMotion ? DRAFT_DELAY_MS.reduced : DRAFT_DELAY_MS.full;
    setTimeout(() => this.renderDraft(), delay);
  }

  /** Substitui o skeleton pelo rascunho e religa os botoes (assets/crisis.js 425-452). */
  private renderDraft(): void {
    this.slot.innerHTML = draftBodyHTML();
    this.button.disabled = false;
    this.button.style.opacity = '1';
    this.button.innerHTML = `${TI.refresh} Gerar novamente`;
    this.generating = false;
    this.bindCopy();
    this.bindRegen();
  }

  /** Liga "Copiar" via clipboard com fallback silencioso (assets/crisis.js 443-449). */
  private bindCopy(): void {
    const copyBtn = qs<HTMLButtonElement>('#crzCopyDraft', this.slot);
    /* v8 ignore next -- draftBodyHTML sempre inclui #crzCopyDraft; guarda defensiva CLAUDE.md rule 11 */
    if (!copyBtn) return;
    on(copyBtn, 'click', () => {
      const text = qs<HTMLElement>('.crz-draft-body', this.slot)?.innerText ?? '';
      if (navigator.clipboard) navigator.clipboard.writeText(text).catch(() => undefined);
      copyBtn.innerHTML = `${TI.check} Copiado`;
      setTimeout(() => {
        copyBtn.innerHTML = `${TI.copy} Copiar`;
      }, COPY_FEEDBACK_MS);
    });
  }

  /** Liga "Regerar" re-acionando o proprio botao gerador (assets/crisis.js 450-451). */
  private bindRegen(): void {
    const regen = qs<HTMLButtonElement>('#crzRegenDraft', this.slot);
    if (regen) on(regen, 'click', () => this.button.click());
  }
}

/** Skeleton "Redigindo nota…" (assets/crisis.js 420-421). */
const DRAFT_SKELETON_HTML =
  `<div class="crz-draft"><div class="crz-draft-head">${TI.spark} Redigindo nota…</div>` +
  '<div class="crz-draft-body"><div class="crz-sk-line crz-skeleton" style="width:96%"></div>' +
  '<div class="crz-sk-line crz-skeleton" style="width:90%"></div>' +
  '<div class="crz-sk-line crz-skeleton" style="width:70%"></div></div></div>';

/** Markup do rascunho final com botoes de acao (assets/crisis.js 425-438). */
function draftBodyHTML(): string {
  return (
    '<div class="crz-draft">' +
    `<div class="crz-draft-head">${TI.spark} Rascunho da nota · revise antes de publicar</div>` +
    '<div class="crz-draft-body">' +
    '<p><b>Nota de esclarecimento</b></p>' +
    '<p>O contrato de reforma do pronto-socorro da Zona Leste segue o cronograma e os valores previstos no edital público, disponível no Portal da Transparência do mandato.</p>' +
    '<p>O trecho que circula nas redes corresponde a um recorte de 18 segundos que omite o contexto da explicação completa. O gabinete reforça seu compromisso com a transparência e coloca toda a documentação à disposição da imprensa e da população.</p>' +
    '</div>' +
    '<div class="crz-draft-actions">' +
    `<button class="crz-btn crz-btn-approve" id="crzCopyDraft">${TI.copy} Copiar</button>` +
    `<button class="crz-btn crz-btn-ghost">${TI.pencil} Editar</button>` +
    `<button class="crz-btn crz-btn-ghost" id="crzRegenDraft">${TI.refresh} Regerar</button>` +
    '</div>' +
    '</div>'
  );
}
